import * as THREE from 'three';

const canvas = document.getElementById('liquid-canvas');

if (canvas) {
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // Renderer
    const renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        alpha: true, // Allow transparency
        antialias: true
    });

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Dynamic Sizing
    const resize = () => {
        const width = canvas.clientWidth;
        const height = canvas.clientHeight;
        if (canvas.width !== width || canvas.height !== height) {
            renderer.setSize(width, height, false);
            if (material.uniforms.resolution) {
                material.uniforms.resolution.value.set(width, height);
            }
        }
    };

    // --- Shader ---
    const vertexShader = `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = vec4(position, 1.0);
        }
    `;

    // Dramatic Fluid Shader (Domain Warping + FBM)
    const fragmentShader = `
        uniform vec2 resolution;
        uniform float time;
        varying vec2 vUv;

        // Noise functions
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

        float snoise(vec2 v) {
            const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
                                0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
                                -0.577350269189626, // -1.0 + 2.0 * C.x
                                0.024390243902439); // 1.0 / 41.0
            vec2 i  = floor(v + dot(v, C.yy) );
            vec2 x0 = v - i + dot(i, C.xx);
            vec2 i1;
            i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
            vec4 x12 = x0.xyxy + C.xxzz;
            x12.xy -= i1;
            i = mod289(i);
            vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
                + i.x + vec3(0.0, i1.x, 1.0 ));
            vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
            m = m*m ;
            m = m*m ;
            vec3 x = 2.0 * fract(p * C.www) - 1.0;
            vec3 h = abs(x) - 0.5;
            vec3 ox = floor(x + 0.5);
            vec3 a0 = x - ox;
            m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
            vec3 g;
            g.x  = a0.x  * x0.x  + h.x  * x0.y;
            g.yz = a0.yz * x12.xz + h.yz * x12.yw;
            return 130.0 * dot(m, g);
        }

        // Fractal Brownian Motion
        float fbm(vec2 p) {
            float f = 0.0;
            float w = 0.5;
            for (int i = 0; i < 5; i++) {
                f += w * snoise(p);
                p *= 2.0;
                w *= 0.5;
            }
            return f;
        }

        void main() {
            vec2 uv = gl_FragCoord.xy / resolution.xy;
            // uv.x *= resolution.x / resolution.y; // Correct aspect buffer

            float t = time * 0.2; // Speed

            // Domain Warping
            vec2 q = vec2(0.);
            q.x = fbm(uv + 0.00 * t);
            q.y = fbm(uv + vec2(1.0));

            vec2 r = vec2(0.);
            r.x = fbm(uv + 1.0 * q + vec2(1.7, 9.2) + 0.15 * t);
            r.y = fbm(uv + 1.0 * q + vec2(8.3, 2.8) + 0.126 * t);

            float f = fbm(uv + r);

            // Color Palette (Mystic Liquid)
            // Dark base, silver/blue highlights
            vec3 color = mix(vec3(0.0, 0.0, 0.0), vec3(0.1, 0.2, 0.4), clamp((f*f)*4.0,0.0,1.0));
            color = mix(color, vec3(0.4, 0.6, 0.8), clamp(length(q),0.0,1.0));
            color = mix(color, vec3(0.9, 0.95, 1.0), clamp(length(r.x),0.0,1.0));

            // Intensity curve
            gl_FragColor = vec4((f*f*f + 0.6 * f*f + 0.5 * f) * color, 0.85); // 0.85 opacity
        }
    `;

    const material = new THREE.ShaderMaterial({
        uniforms: {
            time: { value: 0 },
            resolution: { value: new THREE.Vector2() }
        },
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        transparent: true
    });

    const plane = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    scene.add(plane);

    // Loop
    const clock = new THREE.Clock();

    const animate = () => {
        resize();
        material.uniforms.time.value = clock.getElapsedTime();
        renderer.render(scene, camera);
        requestAnimationFrame(animate);
    };

    animate();
}
